import { MediaLink, MediaLinkType } from "../types/media-link";
import { IMediaLinkService } from "../types/services/media-link-service";
import { DELETE, FetchResult, GET, PATCH, POST } from "../utils/fetch";

type MediaLinksResponse = {
  media_links: MediaLink[];
};

type MediaLinkResponse = {
  media_link: MediaLink;
};

abstract class BaseMediaLinkService implements IMediaLinkService {
  protected abstract vertical: "development" | "chapter";

  async getProjectMediaLinks(projectId: number): Promise<FetchResult<MediaLink[]>> {
    return this.getMediaLinks(`/api/v2/${this.vertical}/projects/${projectId}/media_links`);
  }

  async createProjectMediaLink(
    projectId: number,
    media_type: MediaLinkType,
    url: string,
  ): Promise<FetchResult<MediaLink>> {
    return this.createMediaLink(
      `/api/v2/${this.vertical}/projects/${projectId}/media_links`,
      media_type,
      url,
    );
  }

  async getPropertyMediaLinks(propertyId: number): Promise<FetchResult<MediaLink[]>> {
    return this.getMediaLinks(`/api/v2/${this.vertical}/properties/${propertyId}/media_links`);
  }

  async createPropertyMediaLink(
    propertyId: number,
    media_type: MediaLinkType,
    url: string,
  ): Promise<FetchResult<MediaLink>> {
    return this.createMediaLink(
      `/api/v2/${this.vertical}/properties/${propertyId}/media_links`,
      media_type,
      url,
    );
  }

  async updateMediaLink(id: number, url: string): Promise<FetchResult<MediaLink>> {
    const path = `/api/v2/${this.vertical}/media_links/${id}`,
      { response, error } = await PATCH<MediaLinkResponse>(path, { url });

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.media_link, error: null };
  }

  async deleteMediaLink(id: number): Promise<FetchResult<void>> {
    const path = `/api/v2/${this.vertical}/media_links/${id}`,
      { error } = await DELETE<void>(path);

    if (error) {
      return { response: null, error };
    }

    return { response: null, error: null };
  }

  private async getMediaLinks(url: string): Promise<FetchResult<MediaLink[]>> {
    const { response, error } = await GET<MediaLinksResponse>(url);

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.media_links, error: null };
  }

  private async createMediaLink(
    url: string,
    media_type: MediaLinkType,
    linkUrl: string,
  ): Promise<FetchResult<MediaLink>> {
    const { response, error } = await POST<MediaLinkResponse>(url, {
      media_type,
      url: linkUrl,
    });

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.media_link, error: null };
  }
}

export default BaseMediaLinkService;
